import { type PropsOf } from '@emotion/react';
import {
  Snackbar as MuiSnackbar,
  styled,
  SnackbarContent,
  type SnackbarProps as MuiSnackbarProps,
  Button,
  snackbarContentClasses,
  type SnackbarContentProps as MuiSnackbarContentProps,
  Portal,
  type SxProps,
  Typography,
  type Theme,
  Stack,
  type SnackbarCloseReason,
} from '@mui/material';
import { vars } from '@tgtg/kit';
import { type ReactNode } from 'react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import { isSxArray } from 'types/shared/MaterialUI';

import { SnackbarVariant, type SnackbarAction } from './types';

interface SnackbarProps {
  open: boolean;
  message: MessageDescriptor;
  autoHide: boolean;
  variant: SnackbarVariant;
  icon?: ReactNode;
  values?: PropsOf<typeof FormattedMessage>['values'];
  action?: SnackbarAction;
  autoHideDuration: number | null;
  onClose: (delay?: number) => void;
  anchorOrigin?: MuiSnackbarProps['anchorOrigin'];
  messageTitle?: MessageDescriptor;
  sx?: SxProps<Theme>;
}

interface SnackbarContentProps extends Omit<MuiSnackbarContentProps, 'variant'> {
  snackbarVariant?: SnackbarVariant;
}

const StyledActionButton = styled(Button, {
  shouldForwardProp: (prop: string) => !['snackbarVariant'].includes(prop),
})<{ snackbarVariant: SnackbarProps['variant'] }>(({ snackbarVariant, theme: { spacing } }) => ({
  display: 'flex',
  minWidth: vars.size.xxlarge,
  minHeight: vars.size.xlarge,
  maxHeight: vars.size.huge,
  paddingX: spacing(vars.spacing.neutral.medium),
  justifyContent: 'center',
  gap: spacing(vars.spacing.neutral.xsmall),
  borderRadius: vars.radius.full,
  backgroundColor:
    snackbarVariant === SnackbarVariant.LIGHT
      ? vars.color.surface.base
      : vars.color.surface.inverse,
  color:
    snackbarVariant === SnackbarVariant.LIGHT
      ? vars.color.content.primary
      : vars.color.content.inverse,
  '&:hover': {
    backgroundColor: vars.color.surface.interactive.stateHover,
  },
}));

const StyledSnackbarContent = styled(SnackbarContent, {
  shouldForwardProp: (prop) => prop !== 'snackbarVariant',
})<SnackbarContentProps>(({ snackbarVariant }) => ({
  padding: vars.spacing.neutral.large,
  boxShadow: vars.elevation.regular,
  maxWidth: '280px',
  backgroundColor:
    snackbarVariant === SnackbarVariant.LIGHT
      ? vars.color.surface.inverse
      : vars.color.surface.negativeBold,
}));

const Snackbar = ({
  message,
  messageTitle,
  icon,
  open,
  values,
  autoHide,
  autoHideDuration,
  variant,
  action,
  onClose,
  anchorOrigin,
  sx = [],
}: SnackbarProps) => {
  const handleOnClose = (event: React.SyntheticEvent | Event, reason: SnackbarCloseReason) => {
    if (!autoHide && reason === 'clickaway') return;
    onClose();
  };

  const handleOnActionClick = () => {
    onClose();
    action?.onClick?.();
  };

  return (
    <Portal>
      <MuiSnackbar
        data-testid='snackbar'
        open={open}
        onClose={handleOnClose}
        anchorOrigin={anchorOrigin}
        autoHideDuration={autoHideDuration}
        sx={[
          {
            [`.${snackbarContentClasses.root}`]: {
              borderRadius: vars.radius.medium,
              width: '100%',
            },
            [`.${snackbarContentClasses.message}`]: {
              padding: 0,
              flex: 1,
              minWidth: 0,
              display: 'flex',
            },
          },
          ...(isSxArray(sx) ? sx : [sx]),
        ]}
      >
        <StyledSnackbarContent
          snackbarVariant={variant}
          message={
            <Stack
              direction='row'
              spacing={vars.spacing.neutral.medium}
              sx={{ justifyContent: 'space-between', alignItems: 'center', flex: 1 }}
            >
              <Stack
                direction='row'
                spacing={vars.spacing.neutral.medium}
                sx={{ alignItems: 'center' }}
              >
                {icon}
                {messageTitle ? (
                  <Typography variant='bodyBold2'>
                    <FormattedMessage {...messageTitle} />
                  </Typography>
                ) : null}
                <Typography variant='body2'>
                  <FormattedMessage {...message} values={values} />
                </Typography>
              </Stack>
              {action ? (
                <StyledActionButton
                  snackbarVariant={variant}
                  variant='contained'
                  onClick={handleOnActionClick}
                >
                  <Typography variant='buttonSmall'>
                    <FormattedMessage {...action.label} />
                  </Typography>
                </StyledActionButton>
              ) : null}
            </Stack>
          }
        />
      </MuiSnackbar>
    </Portal>
  );
};

export default Snackbar;
